<template>
  <CModal :show.sync="show" :title="$pgettext('coupon.modal.assign.title', 'Assign Coupon')" centered>
    <div v-if="loadingBatches" class="text-center py-4">
      <CSpinner color="primary"/>
    </div>
    <template v-else-if="availableBatches.length === 0">
      <CAlert color="warning">
        <translate translate-context="coupon.modal.assign">No coupon batches available for this service.</translate>
      </CAlert>
    </template>
    <template v-else>
      <CSelect
          v-if="availableBatches.length > 1"
          :value="form.batchId"
          @update:value="onBatchChange"
          :options="batchOptions"
          :label="$pgettext('coupon.modal.assign.label', 'Coupon Type')"
          :placeholder="$pgettext('coupon.modal.assign.placeholder', 'Select a coupon type')"
          class="mb-3"
      />
      <CCard v-if="selectedBatch" class="bg-light">
        <CCardBody>
          <h6 class="mb-2">{{ selectedBatch.name }}</h6>
          <p class="mb-2 text-muted">{{ selectedBatch.promotion_rule.name }}</p>
          <p v-if="selectedBatch.description" class="mb-3 small text-muted">{{ selectedBatch.description }}</p>
          <div v-if="canOverrideAmount" class="form-group mb-0">
            <label class="small">
              <translate translate-context="coupon.modal.assign.label">Discount Amount</translate>
            </label>
            <div class="input-group">
              <input
                  v-model.number="form.amount"
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control"
              />
              <div class="input-group-append">
                <span class="input-group-text">{{ currency }}</span>
              </div>
            </div>
          </div>
          <div v-else class="d-flex align-items-center">
            <CBadge color="success">
              {{ defaultAmount | currency(currency) }}
            </CBadge>
          </div>
        </CCardBody>
      </CCard>
    </template>
    <template #footer>
      <CButton color="secondary" @click="close">
        <translate translate-context="coupon.modal.assign.actions">Close</translate>
      </CButton>
      <PButton
          color="primary"
          class="mr-3"
          :disabled="!canAssign"
          @click="assign"
      >
        <translate translate-context="coupon.modal.assign.actions">Assign</translate>
      </PButton>
    </template>
  </CModal>
</template>

<script>
import {mapActions, mapGetters} from "vuex"
import PButton from "@/components/PButton.vue"

export default {
  name: "AssignCouponModal",
  components: {PButton},
  props: {
    userId: {
      type: Number,
      required: true
    },
    picaServiceId: {
      type: Number,
      required: true
    },
    picacode: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      show: false,
      selectedBatch: null,
      form: {
        batchId: null,
        amount: null
      }
    }
  },
  computed: {
    ...mapGetters('edgeCoupon', [
      'availableBatches',
      'loading'
    ]),
    ...mapGetters('event', [
      'eventCurrency'
    ]),
    currency() {
      return this.eventCurrency || 'EUR'
    },
    loadingBatches() {
      return this.loading.batches
    },
    batchOptions() {
      return this.availableBatches.map(batch => ({
        value: batch.id,
        label: batch.name
      }))
    },
    defaultAmount() {
      if (!this.selectedBatch) return 0
      return this.selectedBatch.promotion_rule.effect_params.amount || 0
    },
    canOverrideAmount() {
      return this.selectedBatch?.allow_effect_override === true
    },
    canAssign() {
      return !!this.form.batchId
    }
  },
  methods: {
    ...mapActions('edgeCoupon', [
      'fetchBatches',
      'redeemCoupon',
      'fetchRedemptions'
    ]),
    async open() {
      this.resetForm()
      this.show = true
      await this.fetchBatches(this.picaServiceId)
      if (this.availableBatches.length === 1) {
        this.selectBatch(this.availableBatches[0])
      }
    },
    onBatchChange(batchId) {
      const batch = this.availableBatches.find(b => b.id === batchId)
      if (batch) this.selectBatch(batch)
    },
    selectBatch(batch) {
      this.selectedBatch = batch
      this.form.batchId = batch.id
      this.form.amount = batch.promotion_rule.effect_params.amount || 0
    },
    close() {
      this.show = false
    },
    resetForm() {
      this.selectedBatch = null
      this.form.batchId = null
      this.form.amount = null
    },
    async assign() {
      try {
        const params = {
          batchId: this.form.batchId,
          userId: this.userId,
          picacode: this.picacode
        }
        if (this.canOverrideAmount && this.form.amount !== this.defaultAmount) {
          params.amount = this.form.amount
        }

        const redemption = await this.redeemCoupon(params)

        this.notifySuccess(
            this.$gettextInterpolate(
                this.$pgettext('coupon.toast', 'Coupon assigned successfully: %{code}'),
                {code: redemption.coupon.code}
            )
        )

        await this.fetchRedemptions({ userId: this.userId, picacode: this.picacode })
        this.close()
      } catch (error) {
        const message = error.response?.data?.detail || error.message
        this.notifyError(message)
      }
    }
  }
}
</script>
